import { useMemo } from 'react';
import { GitBranch } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import TrackBuilder from '../builder/TrackBuilder';
import { WorkflowTabProps, VisitorTrackNode } from './types';
import AgentTabHeader from './AgentTabHeader';

function buildChildrenMap(nodes: VisitorTrackNode[]) {
  const map = new Map<string | null, VisitorTrackNode[]>();
  for (const n of nodes) {
    const parent = n.parent_system_name ?? null;
    const list = map.get(parent) || [];
    list.push(n);
    map.set(parent, list);
  }
  return map;
}

export default function WorkflowTab({
  agId,
  form,
  goal,
  persona,
  trackNodes,
  setTrackNodes,
  enableTracks,
  setEnableTracks,
  enableSkills,
  setNeedsUpdate,
  actions
}: WorkflowTabProps) {
  const childrenByParent = useMemo(() => buildChildrenMap(trackNodes || []), [trackNodes]);
  const roots = childrenByParent.get(null) || [];
  const root = roots[0];
  const rootChildren = root ? (childrenByParent.get(root.system_name) || []) : [];
  const canEnableTracks = !!root && rootChildren.length >= 2;

  const handleToggle = (value: boolean) => {
    if (value && enableSkills) {
      toast.error(
        'Disable Skills first — Skills and the Visitor Workflow cannot run at the same time.',
        toastConfig
      );
      return;
    }
    if (value && !canEnableTracks) {
      toast.error(
        `Add at least two sub-tracks under ${(root?.name || '').trim() || 'the top-level track'} to enable the workflow.`,
        toastConfig
      );
      return;
    }
    setEnableTracks(value);
    setNeedsUpdate(true);
  };

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={GitBranch}
        title="Workflow"
        description="Define visitor tracks and route conversations based on visitor type."
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="workflow-toggle" className="cursor-pointer text-sm font-medium">
              {enableTracks ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="workflow-toggle"
              checked={enableTracks}
              onCheckedChange={handleToggle}
              disabled={!enableTracks && !canEnableTracks}
            />
          </div>
        }
      />

      <div className="mx-auto w-full max-w-4xl py-6">
        <TrackBuilder
          agentId={agId}
          agentGoal={goal}
          agentPersona={persona}
          trackNodes={trackNodes}
          setTrackNodes={setTrackNodes}
          enableTracks={enableTracks}
          setEnableTracks={setEnableTracks}
          setNeedsUpdate={setNeedsUpdate}
          form={form}
          actions={actions}
        />
      </div>
    </div>
  );
}
